import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Button, Stack } from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { getTheme } from '@parsys/frontend_library';
import cx from 'classnames';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import ExtendButton from '../ExtendButton/ExtendButton';
import AppointmentForm from 'FRONTEND/components/Calendar/AppointmentForm/AppointmentForm';
import AppointmentList from 'FRONTEND/components/Calendar/AppointmentList/AppointmentList';
import MiniSocketListener from 'FRONTEND/components/MiniSocketListener/MiniSocketListener';
import { resetBookingForm } from 'FRONTEND/reducers/bookingSlice';
import { changeUnrestrictedMode, goToPatientForm, goToUnrestrictedForm } from 'FRONTEND/reducers/calendarSlice';
import { endVideoConference } from 'FRONTEND/reducers/rightPanelSlice';
import { selectHasRight } from 'FRONTEND/reducers/userSlice';
import { THEME_APP_NAME } from 'FRONTEND/utils/constants';
import i18n from 'FRONTEND/utils/i18n';
import { RIGHTS } from 'FRONTEND/utils/rights';
import { PATIENT_APPOINTMENT_CREATION_PATH } from 'FRONTEND/utils/routes';
import { PJAX_MAIN_CONTAINER_SELECTOR } from 'LEGACY/constants';

const theme = getTheme({ app: THEME_APP_NAME, branding: window.branding });

// TODO: avoid providing a full theme for just a few style overrides
const smallFontTheme = createTheme({
  ...theme,
  typography: {
    ...theme.typography,
    fontSize: 12, // Réduction globale de la police
  },
  components: {
    ...theme.components,
    MuiPaper: {
      defaultProps: {
        elevation: 1,
      },
    },
  },
});

/**
 * A component representing the right panel with appointment and video conference functionalities.
 *
 * @param {Object} props - The component props.
 * @param {string} props.rtcServerUrl - The URL for the RTC server.
 * @param {boolean} props.rtcEnabled - Whether RTC is enabled.
 * @returns {JSX.Element} - The rendered RightPanel component.
 */
const RightPanel = ({
  rtcServerUrl,
  rtcEnabled,
}) => {
  const dispatch = useDispatch();
  // Appointment management
  const videoConferenceUrl = useSelector((state) => state.rightPanel.videoConferenceUrl);
  const isRightPanelShown = useSelector((state) => state.rightPanel.isShown);
  const isExtended = useSelector((state) => state.rightPanel.isExtended);
  const isMaxExtended = useSelector((state) => state.rightPanel.isMaxExtended);
  const isSidePanelMounted = useSelector(state => state.sidePanel.isMounted);
  const isSidePanelExtended = useSelector(state => state.sidePanel.isExtended);
  const [isAppointmentFormVisible, setIsAppointmentFormVisible] = useState(false);
  const canCreate = useSelector((state) => selectHasRight(state, RIGHTS.APPOINTMENTS_CREATE));
  const canCreateAsPatient = useSelector((state) => selectHasRight(state, RIGHTS.APPOINTMENTS_CREATE_AS_PATIENT));
  const isButtonVisible = canCreate || canCreateAsPatient;


  const handleNewAppointment = () => {
    if (canCreateAsPatient) {
      // Patient portal scenario: open the appointment form page itself
      dispatch(goToPatientForm());
      $.pjax({ url: PATIENT_APPOINTMENT_CREATION_PATH, container: PJAX_MAIN_CONTAINER_SELECTOR });
    } else {
      // Loose (unrestricted) appointment scenario: open the appointment form in the right panel
      dispatch(goToUnrestrictedForm());
      setIsAppointmentFormVisible(true);
    }
  };

  const closeAppointmentForm = () => {
    dispatch(resetBookingForm());
    setIsAppointmentFormVisible(false);
  };

  const closeVideoConsultation = () => {
    dispatch(endVideoConference());
  };

  useEffect(() => {
    if (!rtcEnabled) {
      dispatch(changeUnrestrictedMode({ isUnrestrictedMode: true }));
    }
  }, []);

  const rightPanelCx = cx('rightPanel', {
    rightPanel_collapsed: !isRightPanelShown,
    rightPanel_rtc: rtcEnabled,
    rightPanel_conferenceIsOn: isExtended,
    rightPanel_maxExtended: isMaxExtended,
    rightPanel_withSidePanel: isSidePanelMounted && !isSidePanelExtended,
    rightPanel_withExtendedSidePanel: isSidePanelMounted && isSidePanelExtended,
  });

  return (
    <Stack id='rightPanel-container' className={rightPanelCx}>
      {videoConferenceUrl || rtcEnabled ? (
        <>
          {!rtcEnabled && (
            <Button variant='contained' sx={{ m: 1, width: '80%' }} color='primary' onClick={closeVideoConsultation} startIcon={<ArrowBackIcon />}>{i18n._('Back')}</Button>
          )}
          <ExtendButton />
          <iframe id='rtc-iframe' className='rtcIframe' src={videoConferenceUrl || rtcServerUrl} allow='autoplay; camera; microphone; geolocation; fullscreen; display-capture' />
        </>
      ) : (
        <ThemeProvider theme={smallFontTheme}>
          <Stack sx={{ fontSize: '0.5rem', backgroundColor: '#fff', height: '100%', width: '100%' }}>
            {isAppointmentFormVisible ? (
              <AppointmentForm forRightPanel onCreateAppointmentSettled={closeAppointmentForm} />
            ) : (
              <>
                <Stack spacing={1} sx={{ p: '10px', overflow: 'auto' }}>
                  <AppointmentList
                    rtcServerUrl={rtcServerUrl}
                    layout='vertical'
                    isSquare={false}
                    displayMode='panel'
                    filterUnrestricted={!canCreateAsPatient}
                  />
                </Stack>
                {isButtonVisible && (
                  <Button sx={{ m: 1 }} variant='contained' color='primary' onClick={handleNewAppointment}>{i18n._('New Appointment')}</Button>
                )}
              </>
            )}
            <MiniSocketListener
              signalingUrl={rtcServerUrl}
            />
          </Stack>
        </ThemeProvider>
      )}
    </Stack>
  );
};

export default RightPanel;
